import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import DropdownField from 'pf9-ui-components/built/components/validatedForm/DropdownField'
import TextField from 'pf9-ui-components/built/components/validatedForm/TextField'
import ToggleSwitchField from 'pf9-ui-components/built/components/validatedForm/ToggleSwitchField'
import Badge from 'pf9-ui-components/built/elements/badge/Badge'
import AsyncDropdown from 'pf9-ui-components/built/elements/dropdown/AsyncDropdown'
import type Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { prop } from 'ramda'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { listImages } from 'app/plugins/openstack/components/images/actions'
import { imagesSelector } from 'app/plugins/openstack/components/images/selectors'
import type { RootState } from 'app/store'
import Alert from 'core/components/Alert'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import useListAction from 'core/hooks/useListAction'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import { listKaapiConfigMaps } from 'k8s/components/kaapi/config-maps/actions'
import { kaapiConfigMapsSelector } from 'k8s/components/kaapi/config-maps/selectors'
import { generateImageOptions, getK8sImages } from '../helper'
import { extractSupportedK8sVersions } from './helpers'

interface AddonFieldProps {
  id: string
  label: string
  description?: string
  params: Record<any, any>
  getParamsUpdater: (value: any) => void
  disabled?: boolean
  badgeText?: string
  tooltipMsg?: string
  info?: string
  customTooltipInfoBody?: React.ReactNode
  hideToggle?: boolean
}

const AddonField = ({
  id,
  label,
  description,
  params,
  getParamsUpdater,
  disabled,
  children,
  badgeText,
  tooltipMsg,
  info,
  customTooltipInfoBody,
  hideToggle,
}: React.PropsWithChildren<AddonFieldProps>) => {
  const classes = useAddonFieldStyles()

  const ToggleComponent = !hideToggle ? (
    <ToggleSwitchField
      id={id}
      value={params[id]}
      onChange={getParamsUpdater(id)}
      disabled={disabled}
    />
  ) : null

  return (
    <div className={classes.addonContainer}>
      {tooltipMsg ? (
        <Tooltip message={tooltipMsg}>{ToggleComponent}</Tooltip>
      ) : (
        <>{ToggleComponent}</>
      )}
      <div className={classes?.addonField}>
        <Text variant="caption1">{label}</Text>
        {info ||
          (customTooltipInfoBody && (
            <HintTooltip customBody={customTooltipInfoBody} message={info} />
          ))}
        {badgeText && <Badge className={classes.badge} text={badgeText} variant="default" />}
        <div className={classes.desciptionAndOptions}>
          <Text variant="body2">{description}</Text>
          {children}
        </div>
      </div>
    </div>
  )
}

const useAddonFieldStyles = makeStyles<Theme>((theme) => ({
  addonContainer: {
    display: 'grid',
    gridTemplateColumns: '32px 1fr',
    gridGap: theme.spacing(2),
    alignItems: 'start',
  },
  desciptionAndOptions: {
    display: 'grid',
    gridAutoFlow: 'row',
    gridGap: theme.spacing(3),
  },
  addonField: {
    display: 'grid',
    gridAutoFlow: 'row',
    gridGap: theme.spacing(1),
  },
  badge: {
    marginTop: theme.spacing(2),
    backgroundColor: '#ECFBFA',
    border: '1px solid #ECFBFA',
  },
}))

interface CloudspadceConfigurationFieldsProps {
  params: Record<any, any>
  getParamsUpdater: (key: string) => (value: any) => void
  isOpenStackCluster?: boolean
  isGPUFlavorSelected?: boolean
  hideK8sVersion?: boolean
  isBYOCluster?: boolean
}

export default function ClusterConfigFields({
  params,
  getParamsUpdater,
  isOpenStackCluster = false,
  isGPUFlavorSelected = false,
  hideK8sVersion = false,
  isBYOCluster = false,
}: CloudspadceConfigurationFieldsProps) {
  const classes = useStyles()
  const { activeKaapiTenant } = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const namespace = activeKaapiTenant
  const [error, setError] = useState<{ title: string; message: string } | null>(null)
  const [k8sImages, setK8sImages] = useState<string[]>([])

  // Load Config Maps
  const { loading: loadingConfigMaps } = useListAction(listKaapiConfigMaps, {
    params: {
      namespace,
    },
    requiredParams: ['namespace'],
  })
  const configMaps = useSelector(kaapiConfigMapsSelector)
  const supportedK8sVersions = useMemo(() => extractSupportedK8sVersions(configMaps), [configMaps])

  // Load Images
  const { loading: loadingImages } = useListAction(listImages)
  const images = useSelector(imagesSelector)

  const k8sVersionOptions = useMemo(
    () =>
      supportedK8sVersions?.map((version) => ({
        label: version,
        value: version,
      })),
    [supportedK8sVersions],
  )

  const handleK8sVersionChange = (version: string) => {
    setError(null)
    getParamsUpdater('k8sVersion')(version)

    // Image is only required for non OpenStack clusters
    if (!isOpenStackCluster) return
    const k8sImages = getK8sImages({
      k8sVersion: version,
      setError,
      images,
      gpuAddons: params?.gpuAddons,
    })
    // Get matching images, if not found set error
    setK8sImages(k8sImages)
    getParamsUpdater('image')(k8sImages?.length ? k8sImages[0] : '')
  }

  const handleGPUChange = (gpu: boolean) => {
    // If GPU flavor is selected, don't allow disabling GPU addons
    if (isGPUFlavorSelected) {
      return
    }

    setError(null)
    getParamsUpdater('gpuAddons')(gpu)

    // Image is only required for non OpenStack clusters
    if (!isOpenStackCluster) return
    const k8sImages = getK8sImages({
      k8sVersion: params?.k8sVersion,
      setError,
      images,
      gpuAddons: gpu,
    })
    // Get matching images, if not found set error
    setK8sImages(k8sImages)
    getParamsUpdater('image')(k8sImages?.length ? k8sImages[0] : '')
  }

  // Set default image if k8sImages are loaded
  useEffect(() => {
    if (k8sImages?.length > 0 && !params?.image) {
      getParamsUpdater('image')(k8sImages[0])
    }
  }, [k8sImages, params?.image])

  const imageOptions = useMemo(() => {
    return generateImageOptions(k8sImages)
  }, [k8sImages])

  return (
    <div className={classes.clusterConfigFields}>
      {!hideK8sVersion && (
        <>
          {error && <Alert variant="error" title={error.title} message={error.message} />}
          <div className={classes.k8sVersion}>
            <DropdownField
              id="k8sVersion"
              label="Kubernetes Version"
              DropdownComponent={AsyncDropdown}
              items={k8sVersionOptions}
              value={params?.k8sVersion}
              onChange={handleK8sVersionChange}
              required
              selectFirst
              loading={loadingConfigMaps}
            />
            {/* Show image selector only for OpenStack clusters */}
            {isOpenStackCluster && (
              <DropdownField
                id="image"
                label="Available Images"
                placeholder={k8sImages?.length === 0 ? 'No Images Available' : 'Select an Image'}
                DropdownComponent={AsyncDropdown}
                items={imageOptions}
                value={params?.image}
                onChange={getParamsUpdater('image')}
                selectFirst
                required
                loading={loadingImages}
              />
            )}
          </div>
        </>
      )}
      {params?.controlPlaneType === 'custom' && isBYOCluster && (
        <>
          <hr className={classes.hr} />
          <div className={classes.vipTextFields}>
            <TextField
              id="vip"
              label="Virtual IP"
              info={
                <Text variant="body2" className={classes.infoText}>
                  An available IP address{' '}
                  <b>from the network on which your control plane hosts reside. </b>
                  kube-vip <b>will advertise it to </b>provide a stable Kubernetes API server
                  endpoint for your cluster.{' '}
                  <b>
                    Ensure that this IP address is reserved for this cluster and not already
                    assigned to another host or part of a DHCP pool.
                  </b>
                </Text>
              }
              value={params?.vip}
              onChange={getParamsUpdater('vip')}
              required
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              enterKeyHint={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              crossOrigin={undefined}
            />
            <TextField
              id="nip"
              label="Network Interface"
              info="The name of the network interface on the control plane hosts that kube-vip uses to advertise the virtual IP via ARP (for example, ens3). The interface name must be the same on all control plane hosts."
              value={params?.networkInterface}
              onChange={getParamsUpdater('networkInterface')}
              required
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              enterKeyHint={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              crossOrigin={undefined}
            />
          </div>
        </>
      )}
      {params?.controlPlaneType === 'custom' && (
        <>
          <hr className={classes.hr} />
          <TextField
            id="proxy"
            label="Proxy"
            placeholder="https:///username:password@host:port"
            value={params?.proxyValue}
            onChange={getParamsUpdater('proxyValue')}
            nonce={undefined}
            onResize={undefined}
            onResizeCapture={undefined}
            enterKeyHint={undefined}
            info="Enter the proxy URL in the format 'http(s)://username:password@host:port'. Leave blank if no proxy is required."
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
            crossOrigin={undefined}
          />
        </>
      )}
      <hr className={classes.hr} />
      <AddonField
        id="enableK8sFlags"
        label="Kubernetes Flags"
        description="Customize kube-api flags, Controller Manager and Scheduler Flags using cluster API."
        params={params}
        getParamsUpdater={getParamsUpdater}
      >
        {params?.enableK8sFlags && (
          <div className={classes.textFields}>
            <TextField
              id="apiServerFlags"
              label="API Server Flags"
              value={params?.apiServerFlags}
              onChange={getParamsUpdater('apiServerFlags')}
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              enterKeyHint={undefined}
              info="Enter a comma separated list of supported kube-apiserver. Eg. --request-timeout=2m0s,--kubelet-timeout=20s"
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              crossOrigin={undefined}
            />
            <TextField
              id="schedulerFlags"
              label="Scheduler Flags"
              value={params?.schedulerFlags}
              onChange={getParamsUpdater('schedulerFlags')}
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              enterKeyHint={undefined}
              info="Enter a comma separated list of supported kube-scheduler flags. Eg. --kube-api-burst=120,--log_file_max_size=3000"
              crossOrigin={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />
            <TextField
              id="controllerManagerFlags"
              label="Controller Manager Flags"
              value={params?.controllerManagerFlags}
              onChange={getParamsUpdater('controllerManagerFlags')}
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              enterKeyHint={undefined}
              info="Enter a comma separated list of supported kube-controller-manager flags. Eg. --large-cluster-size-threshold=60,--concurrent-statefulset-syncs=10"
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              crossOrigin={undefined}
            />
          </div>
        )}
      </AddonField>
      <hr className={classes.hr} />
      <AddonField
        id="enableAddons"
        label="Add-Ons"
        description="Customize add-ons using cluster API add-on configuration manifests."
        params={params}
        getParamsUpdater={() => (value) => {}}
        // getParamsUpdater={() => (value) => handleEnableAddonsChange(value)}
        hideToggle
      >
        {params?.enableAddons && (
          <div className={classes.addonsFieldContainer}>
            <div className={classes.column}>
              <div className={classes.addonCategoryHeader}>
                <Text component="figure" variant="caption1" className={classes.numberCircle}>
                  1
                </Text>
                <Text variant="body2">Core Add-ons</Text>
              </div>
              <div className={clsx(classes.column, classes.coreAddons)}>
                <Tooltip
                  message="Disabling CoreDNS is currently not supported"
                  align={{ vertical: 'middle', horizontal: 'left' }}
                >
                  <ToggleSwitchField
                    id="coreDns"
                    label="CoreDNS"
                    value={params?.coreDns}
                    // onChange={getParamsUpdater('coreDns')}
                    className={classes.disabledRadioButton}
                    data-testid="coredns-toggle"
                  />
                </Tooltip>
                <Tooltip
                  message="Disabling Calico is currently not supported"
                  align={{ vertical: 'middle', horizontal: 'left' }}
                >
                  <ToggleSwitchField
                    id="calico"
                    label="Calico"
                    value={params?.calico}
                    // onChange={getParamsUpdater('calico')}
                    className={classes.disabledRadioButton}
                    data-testid="calico-toggle"
                  />
                </Tooltip>
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.addonCategoryHeader}>
                <Text component="figure" variant="caption1" className={classes.numberCircle}>
                  2
                </Text>
                <Text variant="body2">Load Balancers</Text>
              </div>
              <div className={clsx(classes.column, classes.coreAddons)}>
                <div className={classes.column2}>
                  <ToggleSwitchField
                    id="metallb"
                    label="MetalLB"
                    value={params?.metallb}
                    onChange={getParamsUpdater('metallb')}
                    data-testid="metallb-toggle"
                  />
                  {!isBYOCluster && (
                    <HintTooltip
                      message="If you disable MetalLB, the cluster will not have a load balancer."
                      data-testid="metallb-tooltip"
                    />
                  )}
                </div>
              </div>
            </div>
            <div className={classes.column}>
              <div className={classes.addonCategoryHeader}>
                <Text component="figure" variant="caption1" className={classes.numberCircle}>
                  3
                </Text>
                <Text variant="body2">Other Add-ons</Text>
              </div>
              <div className={classes.column}>
                <div className={classes.column2}>
                  <Tooltip
                    message={
                      isGPUFlavorSelected
                        ? "You can't disable GPU addons if you have selected a GPU flavor for any node group."
                        : 'Enabling GPU will enable GPU support for the cluster.'
                    }
                    data-testid="gpu-addons-tooltip"
                  >
                    <ToggleSwitchField
                      id="Nvidia GPU Operator"
                      label="Nvidia GPU Operator"
                      value={params?.gpuAddons}
                      onChange={handleGPUChange}
                      data-testid="gpu-addons-toggle"
                    />
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        )}
      </AddonField>
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  clusterConfigFields: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gridGap: theme.spacing(3),
  },
  hr: {
    height: 1,
    background: theme.components.card.border,
    border: 0,
    margin: theme.spacing(2, 3, 1, 0),
  },
  infoText: {
    color: theme.components.tooltip.text,
  },
  k8sVersion: {
    display: 'grid',
    gridGap: theme.spacing(2),
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'baseline',
  },
  textFields: {
    display: 'grid',
    gridGap: theme.spacing(2),
  },
  vipTextFields: {
    display: 'grid',
    gridGap: theme.spacing(2),
    gridTemplateColumns: '1fr 1fr',
    width: '97%',
  },
  addonsFieldContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 185px)',
    gridGap: theme.spacing(4),
  },
  column: {
    display: 'grid',
    gridGap: theme.spacing(2),
    gridTemplateRows: 'max-content',
  },
  column2: {
    display: 'grid',
    gridGap: theme.spacing(2),
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'center',
  },
  comingSoonFlag: {
    backgroundColor: theme.palette.blue['700'],
    color: theme.palette.common.white,
    padding: theme.spacing(1, 2, 1, 3),
    whiteSpace: 'nowrap',
    zIndex: 10,
    marginRight: theme.spacing(2),
    display: 'inline-block',
    clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 10px 50%)', // Creates the transparent triangle on the left
  },
  addonCategoryHeader: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    gridGap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    alignItems: 'center',
  },
  coreAddons: {
    borderRight: `1px solid ${theme.components.card.border}`,
  },
  numberCircle: {
    borderRadius: '50%',
    width: 24,
    height: 24,
    display: 'inline-grid',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.components.wizard.multiStep.bubbleBackground,
    border: `1px solid ${theme.components.wizard.multiStep.bubbleBorder}`,
  },
  disabledRadioButton: {
    cursor: 'not-allowed',
  },
}))
